import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { certApi, type CertRow } from '../../api/certApi';
import CertList from '../certs/CertList';
import RequestCertDialog from '../certs/RequestCertDialog';

const CertificatesTab: React.FC = () => {
    const auth = useAuth();
    const [certs, setCerts] = useState<CertRow[] | undefined>(undefined);
    const [error, setError] = useState<string | undefined>(undefined);
    const [dialogOpen, setDialogOpen] = useState(false);

    const token = auth.user?.access_token ?? '';

    const refresh = useCallback(async () => {
        if (!token) return;
        setError(undefined);
        try {
            const { certs: rows } = await certApi.list(token, 'mtls-client');
            setCerts(rows);
        } catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        }
    }, [token]);

    useEffect(() => { void refresh(); }, [refresh]);

    return (
        <Box>
            <Stack direction='row' justifyContent='space-between' alignItems='center' sx={{ mb: 2 }}>
                <Typography variant='h5'>mTLS client certificates</Typography>
                <Stack direction='row' spacing={1}>
                    <Button variant='outlined' onClick={refresh}>Refresh</Button>
                    <Button variant='contained' onClick={() => setDialogOpen(true)}>Request new</Button>
                </Stack>
            </Stack>
            {error && <Alert severity='error' sx={{ mb: 2 }}>{error}</Alert>}
            {certs === undefined && <CircularProgress />}
            {certs !== undefined && (
                <CertList certs={certs} token={token} onChanged={refresh} />
            )}
            <RequestCertDialog
                open={dialogOpen}
                token={token}
                kind='mtls-client'
                onClose={() => setDialogOpen(false)}
                onRequested={() => { setDialogOpen(false); void refresh(); }}
            />
        </Box>
    );
};

export default CertificatesTab;
